import { Badge, Button } from '@fsd/ui';
import { Link, useParams } from '@tanstack/react-router';
import { CheckCircle2, Package, ShoppingCart, Star, Truck } from 'lucide-react';
import { useEffect } from 'react';
import { CartSheet, Footer, Header, SEO } from '@/components';
import { useTranslation } from '@/i18n/hooks';
import { getLocalizedString, getLocalizedStringArray, useCart } from '@/utils';
import { ProductImageGallery } from './productImageGallery';
import { useProductDetail } from './useProductDetail';

export function ProductDetail() {
  const { id, type } = useParams({ from: '/product/$type/$id' });
  const { t, locale } = useTranslation('product');
  const { product, isLoading, error } = useProductDetail(
    id,
    type as 'new' | 'used' | 'buffalo' | 'power-citros' | 'parts'
  );
  const { addItem, setIsOpen } = useCart();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white dark:bg-slate-900">
        <Header />
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8 mt-20">
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-slate-600 dark:text-slate-400">{t('loading')}</p>
            </div>
          </div>
        </div>
        <Footer />
        <CartSheet />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-white dark:bg-slate-900">
        <Header />
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8 mt-20">
          <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-4">
              {t('notFound.title')}
            </h1>
            <p className="text-slate-600 dark:text-slate-400 mb-6">{t('notFound.description')}</p>
            <Link to="/catalog" search={{ category: 'all', page: 1 }}>
              <Button variant="outline">{t('notFound.backToCatalog')}</Button>
            </Link>
          </div>
        </div>
        <Footer />
        <CartSheet />
      </div>
    );
  }

  const name = getLocalizedString(product.name, locale);
  const description = getLocalizedString(product.description, locale);
  const features = getLocalizedStringArray(product.features, locale);
  const condition = getLocalizedString(product.condition, locale);
  const specifications = product.specifications;
  const warranty = getLocalizedString(product.warranty, locale);
  const shipping = getLocalizedString(product.shipping, locale);
  const videoUrl = product.video;

  // Use images array if available, otherwise fallback to single image
  const images = product.images && product.images.length > 0 ? product.images : [product.image];

  // SEO data
  const productImage = images[0] || product.image;
  const productUrl = `https://fronteirasuldrones.com/product/${type}/${id}`;
  const shortDescription =
    description.length > 160 ? `${description.substring(0, 157)}...` : description;
  const typeKeyword =
    type === 'new'
      ? 'drone novo'
      : type === 'used'
        ? 'drone usado'
        : type === 'buffalo'
          ? 'linha buffalo'
          : 'peças e acessórios';

  const handleAddToCart = () => {
    addItem(product, 1, type === 'used');
    setIsOpen(true);
  };

  /**
   * Render all specifications dynamically from the specifications array
   */
  const renderAllSpecifications = (): React.ReactElement[] => {
    if (!specifications || !Array.isArray(specifications)) return [];

    return specifications
      .map((spec) => {
        if (!spec) return null;

        const label = getLocalizedString(spec.label, locale);
        const value = getLocalizedString(spec.value, locale);

        // Skip if no label or value (even after fallback)
        if (!label || label.trim() === '' || !value || value.trim() === '') return null;

        return (
          <div
            key={spec.id}
            className="flex flex-col border-b border-slate-200 py-3 dark:border-slate-700 sm:flex-row sm:items-center sm:justify-between"
          >
            <dt className="text-sm font-medium text-slate-600 dark:text-slate-400">{label}</dt>
            <dd className="mt-1 text-sm text-slate-900 dark:text-slate-100 sm:mt-0 sm:text-right">
              {value}
            </dd>
          </div>
        );
      })
      .filter((item): item is React.ReactElement => item !== null);
  };

  /**
   * Check if specifications array has any non-empty values
   */
  const hasSpecifications = (): boolean => {
    if (!specifications || !Array.isArray(specifications)) return false;

    // Check if at least one specification has both label and value in any locale
    return specifications.some((spec) => {
      if (!spec || !spec.label || !spec.value) return false;

      // Check if the spec has both label and value in at least one locale
      const hasLabel = Object.values(spec.label).some((val) => val && val.trim() !== '');
      const hasValue = Object.values(spec.value).some((val) => val && val.trim() !== '');

      return hasLabel && hasValue;
    });
  };

  /**
   * Detects whether the video URL should be rendered as embed (iframe)
   * or as a direct file source (video tag).
   */
  const stripQueryAndHash = (value: string): string => {
    const [withoutQuery] = value.split('?');
    const baseValue = withoutQuery ?? value;
    const [withoutHash] = baseValue.split('#');
    return withoutHash ?? baseValue;
  };

  const getVideoSource = (
    url: string
  ): { type: 'embed'; url: string } | { type: 'file'; url: string } | null => {
    if (!url) return null;

    // YouTube
    const youtubeRegex =
      /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/;
    const youtubeMatch = url.match(youtubeRegex);
    if (youtubeMatch) {
      return { type: 'embed', url: `https://www.youtube.com/embed/${youtubeMatch[1]}` };
    }

    // Vimeo
    const vimeoRegex = /(?:vimeo\.com\/)(\d+)/;
    const vimeoMatch = url.match(vimeoRegex);
    if (vimeoMatch) {
      return { type: 'embed', url: `https://player.vimeo.com/video/${vimeoMatch[1]}` };
    }

    // If already an embed URL, return as is
    if (url.includes('youtube.com/embed') || url.includes('player.vimeo.com')) {
      return { type: 'embed', url };
    }

    // Direct video file (mp4, webm, ogg, mov, m4v)
    const supportedVideoExtensions = /\.(mp4|webm|ogg|mov|m4v)$/i;

    try {
      const parsedUrl = new URL(url);
      const decodedPath = decodeURIComponent(parsedUrl.pathname);
      const pathname = stripQueryAndHash(decodedPath);

      if (supportedVideoExtensions.test(pathname)) {
        return { type: 'file', url };
      }
    } catch {
      // Ignore URL parsing errors and fall back to raw check below
    }

    const normalizedUrl = stripQueryAndHash(url);
    if (supportedVideoExtensions.test(normalizedUrl)) {
      return { type: 'file', url };
    }

    return null;
  };

  /**
   * Returns MIME type for known direct video extensions
   */
  const getVideoMimeType = (url: string): string | undefined => {
    const normalized = stripQueryAndHash(url.toLowerCase());
    if (normalized.endsWith('.mp4')) return 'video/mp4';
    if (normalized.endsWith('.webm')) return 'video/webm';
    if (normalized.endsWith('.ogg')) return 'video/ogg';
    if (normalized.endsWith('.mov')) return 'video/quicktime';
    if (normalized.endsWith('.m4v')) return 'video/x-m4v';
    return undefined;
  };

  const videoSource = videoUrl ? getVideoSource(videoUrl) : null;

  // Category for breadcrumbs
  const categoryName =
    type === 'new'
      ? 'Drones Novos'
      : type === 'used'
        ? 'Drones Usados'
        : type === 'buffalo'
          ? 'Linha Buffalo'
          : 'Peças e Acessórios';
  const breadcrumbs = [
    { name: 'Início', url: '/' },
    { name: 'Catálogo', url: '/catalog' },
    { name: categoryName, url: `/catalog?category=${type}` },
    { name: name, url: `/product/${type}/${id}` },
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900">
      <SEO
        title={name}
        description={shortDescription}
        keywords={`${name}, drone profissional, ${typeKeyword}, agricultura de precisão, pulverização agrícola`}
        image={productImage}
        url={productUrl}
        type="product"
        product={{
          name,
          currency: 'BRL',
          availability: product.inStock ? 'InStock' : 'OutOfStock',
          condition: type === 'used' ? 'UsedCondition' : 'NewCondition',
          brand: 'Fronteira Sul Drones',
          sku: product.id,
          rating: product.rating,
          reviewCount: 127, // Considere tornar isso dinâmico
        }}
        breadcrumbs={breadcrumbs}
      />
      <Header />

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8 mt-20">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
          {/* Product Image Gallery */}
          <div className="relative">
            <ProductImageGallery
              images={images}
              name={name}
              inStock={product.inStock}
              condition={condition}
              locale={locale}
            />
          </div>

          {/* Product Info */}
          <div className="flex flex-col">
            {/* Rating */}
            {typeof product.rating === 'number' && product.rating > 0 && (
              <div className="mb-4 flex items-center gap-2">
                <div className="flex items-center gap-1">
                  <Star className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                  <span className="text-lg font-medium text-slate-700 dark:text-slate-300">
                    {product.rating}
                  </span>
                </div>
              </div>
            )}

            {/* Product Name */}
            <h1 className="mb-4 text-3xl font-bold text-slate-900 dark:text-slate-100 sm:text-4xl">
              {name}
            </h1>

            {/* Description */}
            <p className="mb-6 text-lg leading-relaxed text-slate-600 dark:text-slate-400">
              {description}
            </p>

            {/* Stock Status */}
            <div className="mb-6">
              <Badge variant={product.inStock ? 'success' : 'danger'}>
                {product.inStock ? t('inStock') : t('outOfStock')}
              </Badge>
            </div>

            {/* Features */}
            {features.length > 0 && (
              <div className="mb-8">
                <h3 className="mb-4 text-lg font-semibold text-slate-900 dark:text-slate-100">
                  {t('features')}
                </h3>
                <ul className="space-y-3">
                  {features.map((feature, index) => (
                    <li
                      key={`feature-${
                        // biome-ignore lint/suspicious/noArrayIndexKey: Index is used as key
                        index
                      }`}
                      className="flex items-center gap-3 text-slate-600 dark:text-slate-400"
                    >
                      <CheckCircle2 className="h-5 w-5 shrink-0 text-primary" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Warranty & Shipping Info */}
            {(warranty || shipping) && (
              <div className="mb-8 space-y-4 rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/50">
                {warranty && (
                  <div className="flex items-start gap-3">
                    <Package className="h-5 w-5 shrink-0 text-primary mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                        {t('warranty')}
                      </p>
                      <p className="text-sm text-slate-600 dark:text-slate-400">{warranty}</p>
                    </div>
                  </div>
                )}
                {shipping && (
                  <div className="flex items-start gap-3">
                    <Truck className="h-5 w-5 shrink-0 text-primary mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                        {t('shipping')}
                      </p>
                      <p className="text-sm text-slate-600 dark:text-slate-400">{shipping}</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Add to Cart Button */}
            {product.inStock && (
              <Button
                onClick={handleAddToCart}
                size="lg"
                className="w-full group"
                variant="secondary"
              >
                <ShoppingCart className="mr-2 h-5 w-5" />
                {locale === 'pt-BR'
                  ? 'Adicionar ao carrinho'
                  : locale === 'en-US'
                    ? 'Add to cart'
                    : 'Añadir al carrito'}
              </Button>
            )}
          </div>
        </div>

        {/* Specifications Section */}
        {hasSpecifications() && (
          <div className="mt-12 border-t border-slate-200 pt-12 dark:border-slate-700">
            <h2 className="mb-6 text-2xl font-bold text-slate-900 dark:text-slate-100">
              {t('specifications')}
            </h2>
            <dl className="divide-y divide-slate-200 dark:divide-slate-700">
              {renderAllSpecifications()}
            </dl>
          </div>
        )}

        {/* Video Section */}
        {videoSource && (
          <div className="mt-16 border-t border-slate-200 pt-12 dark:border-slate-700">
            <div className="mb-8">
              <h2 className="mb-2 text-3xl font-bold text-slate-900 dark:text-slate-100">
                {t('video.title')}
              </h2>
              <p className="text-slate-600 dark:text-slate-400">{t('video.description')}</p>
            </div>
            <div className="relative group">
              {/* Decorative gradient background */}
              <div className="absolute -inset-4 bg-linear-to-br from-primary/10 via-primary/5 to-transparent rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

              {/* Video container with enhanced styling */}
              <div className="relative aspect-video w-full overflow-hidden rounded-xl bg-slate-900 shadow-2xl ring-1 ring-slate-200/50 dark:ring-slate-700/50 transition-all duration-300 group-hover:shadow-primary/20 group-hover:ring-primary/20">
                {videoSource.type === 'embed' ? (
                  <iframe
                    src={videoSource.url}
                    className="h-full w-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title={`${name} - ${t('video.title')}`}
                  />
                ) : (
                  <video
                    className="h-full w-full"
                    controls
                    preload="metadata"
                    playsInline
                    title={`${name} - ${t('video.title')}`}
                  >
                    <source src={videoSource.url} type={getVideoMimeType(videoSource.url)} />
                    <track
                      kind="captions"
                      srcLang="pt-BR"
                      label="Português (Brasil)"
                      src="data:text/vtt;charset=utf-8,WEBVTT%0A%0A"
                      default
                    />
                    Seu navegador não suporta reprodução de vídeo.
                  </video>
                )}

                {/* Subtle overlay gradient at bottom */}
                <div className="absolute inset-x-0 bottom-0 h-20 bg-linear-to-t from-slate-900/20 to-transparent pointer-events-none" />
              </div>
            </div>
          </div>
        )}
      </div>

      <Footer />
      <CartSheet />
    </div>
  );
}
